import React, { useState } from "react";

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth <= 640);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val);

const COLORS = ["#4F7FFA", "#F97316", "#10B981", "#A855F7", "#EF4444", "#F59E0B"];

function getPayoffDate(months) {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function runPayoffSimulation(accounts, extraBudget, order, extraSchedule = []) {
  // Deep copy accounts with remaining balance
  let debts = order.map((id) => {
    const acct = accounts.find((a) => a.id === id);
    return { ...acct, remaining: acct.balance, colorIdx: accounts.findIndex(a => a.id === id) };
  });

  // Build a month->extra map from schedule entries
  // Each entry: { fromMonth, toMonth (or null = forever), amount }
  const getExtraForMonth = (month) => {
    for (let i = extraSchedule.length - 1; i >= 0; i--) {
      const e = extraSchedule[i];
      const from = e.fromMonth || 1;
      const to = e.toMonth || Infinity;
      if (month >= from && month <= to) return e.amount;
    }
    return extraBudget;
  };

  const totalMinimum = debts.reduce((s, d) => s + d.minPayment, 0);
  let month = 0;
  const maxMonths = 600;
  let totalInterestPaid = 0;
  const payoffMonths = {};
  const timeline = [];

  while (debts.some((d) => d.remaining > 0) && month < maxMonths) {
    month++;
    const currentExtra = getExtraForMonth(month);
    let budgetLeft = totalMinimum + currentExtra;
    const snap = { month, debts: [] };

    // Apply minimums first
    for (let d of debts) {
      if (d.remaining <= 0) continue;
      const interest = d.remaining * (d.rate / 100 / 12);
      totalInterestPaid += interest;
      const minPay = Math.min(d.minPayment, d.remaining + interest);
      const principal = minPay - interest;
      d.remaining = Math.max(d.remaining - principal, 0);
      budgetLeft -= minPay;
    }

    // Apply extra to first non-zero debt in order
    for (let d of debts) {
      if (d.remaining <= 0 || budgetLeft <= 0) continue;
      const extra = Math.min(budgetLeft, d.remaining);
      d.remaining = Math.max(d.remaining - extra, 0);
      budgetLeft -= extra;
    }

    // Record payoffs
    for (let d of debts) {
      if (d.remaining === 0 && !payoffMonths[d.id]) {
        payoffMonths[d.id] = month;
      }
    }

    // Redistribute freed minimums — budget recalcs each month from schedule
    snap.debts = debts.map((d) => ({ id: d.id, name: d.name, remaining: d.remaining, colorIdx: d.colorIdx }));
    if (month <= 60) timeline.push(snap);
  }

  return {
    totalInterestPaid,
    totalMonths: month,
    payoffMonths,
    timeline,
    payoffDate: getPayoffDate(month),
  };
}

function buildStrategies(accounts, extraBudget, customOrder, extraSchedule = []) {
  const snowballOrder = [...accounts].sort((a, b) => a.balance - b.balance).map((a) => a.id);
  const avalancheOrder = [...accounts].sort((a, b) => b.rate - a.rate).map((a) => a.id);

  return {
    snowball: runPayoffSimulation(accounts, extraBudget, snowballOrder, extraSchedule),
    avalanche: runPayoffSimulation(accounts, extraBudget, avalancheOrder, extraSchedule),
    custom: customOrder.length === accounts.length
      ? runPayoffSimulation(accounts, extraBudget, customOrder, extraSchedule)
      : null,
  };
}

export default function PayoffStrategy({ accounts, onBack, customOrder: customOrderProp, onCustomOrderChange, activeStrategy: activeStrategyProp, onStrategyChange, onOverridesChange, isPro = false, onUpgrade }) {
  const isMobile = useIsMobile();
  const [extraBudget, setExtraBudget] = useState(() => {
    try { return parseFloat(localStorage.getItem("elde_extra_budget") || "100") || 100; } catch { return 100; }
  });

  const updateExtraBudget = (val) => {
    setExtraBudget(val);
    try { localStorage.setItem("elde_extra_budget", String(val)); } catch {}
    // Remove any overrides that now match the new default
    const cleaned = {};
    Object.entries(monthOverrides).forEach(([k, v]) => { if (v !== val) cleaned[k] = v; });
    if (Object.keys(cleaned).length !== Object.keys(monthOverrides).length) saveMonthOverrides(cleaned);
  };
  const [activeStrategy, setActiveStrategy] = useState(() => {
    if (activeStrategyProp) return activeStrategyProp;
    try { return localStorage.getItem("elde_strategy") || "avalanche"; } catch { return "avalanche"; }
  });
  const [customOrder, setCustomOrder] = useState(() => {
    // First try localStorage directly
    try {
      const saved = JSON.parse(localStorage.getItem("elde_custom_order") || "null");
      if (saved && saved.length === accounts.length && saved.every(id => accounts.find(a => a.id === id))) return saved;
    } catch {}
    // Then try prop
    if (customOrderProp && customOrderProp.length === accounts.length &&
        customOrderProp.every(id => accounts.find(a => a.id === id))) return customOrderProp;
    return accounts.map((a) => a.id);
  });

  const updateStrategy = (s) => {
    setActiveStrategy(s);
    try { localStorage.setItem("elde_strategy", s); } catch {}
    if (onStrategyChange) onStrategyChange(s);
  };
  const updateCustomOrder = (order) => {
    setCustomOrder(order);
    try { localStorage.setItem("elde_custom_order", JSON.stringify(order)); } catch {}
    if (onCustomOrderChange) onCustomOrderChange(order);
  };

  // monthOverrides: { [monthNum]: amount } — only stores months that differ from extraBudget
  const [monthOverrides, setMonthOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem("elde_month_overrides") || "{}"); } catch { return {}; }
  });

  const saveMonthOverrides = (overrides) => {
    setMonthOverrides(overrides);
    try { localStorage.setItem("elde_month_overrides", JSON.stringify(overrides)); } catch {}
    if (onOverridesChange) onOverridesChange(overrides);
  };

  const setMonthAmount = (monthNum, value, currentDefault) => {
    const parsed = parseFloat(value);
    const next = { ...monthOverrides };
    if (value === "" || isNaN(parsed) || parsed === currentDefault) {
      delete next[monthNum]; // remove override — falls back to default
    } else {
      next[monthNum] = parsed;
    }
    saveMonthOverrides(next);
  };

  // Convert monthOverrides map to the schedule array format the simulation expects
  const extraSchedule = Object.entries(monthOverrides).map(([monthNum, amount]) => ({
    id: `month-${monthNum}`,
    fromMonth: parseInt(monthNum),
    toMonth: parseInt(monthNum),
    amount,
  }));

  // Auto-shift: when a new month arrives, shift all overrides forward by 1
  React.useEffect(() => {
    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const stored = localStorage.getItem("elde_schedule_last_month");
    if (stored && stored !== currentKey && Object.keys(monthOverrides).length) {
      const shifted = {};
      Object.entries(monthOverrides).forEach(([num, amt]) => {
        const n = parseInt(num) - 1;
        if (n >= 1) shifted[n] = amt;
      });
      saveMonthOverrides(shifted);
    }
    localStorage.setItem("elde_schedule_last_month", currentKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [showSchedule, setShowSchedule] = useState(false);

  const strategies = buildStrategies(accounts, extraBudget, customOrder, extraSchedule);
  // Also compute baseline (no extra schedule) for comparison
  const baseline = extraSchedule.length > 0 ? buildStrategies(accounts, extraBudget, customOrder, []) : null;
  const current = strategies[activeStrategy];

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...customOrder];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    updateCustomOrder(next);
  };
  const moveDown = (idx) => {
    if (idx === customOrder.length - 1) return;
    const next = [...customOrder];
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    updateCustomOrder(next);
  };

  const strategyCards = [
    {
      key: "avalanche",
      label: "Avalanche",
      icon: "🏔",
      desc: "Pay highest interest first",
      detail: "Mathematically optimal — saves the most money in interest.",
      color: "#4F7FFA",
    },
    {
      key: "snowball",
      label: "Snowball",
      icon: "⛄",
      desc: "Pay smallest balance first",
      detail: "Quick wins keep you motivated as debts disappear faster.",
      color: "#10B981",
    },
    {
      key: "custom",
      label: "Custom",
      icon: "🎯",
      desc: "You choose the order",
      detail: "Drag debts into your preferred payoff order below.",
      color: "#A855F7",
    },
  ];

  const avalancheSavings =
    strategies.snowball && strategies.avalanche
      ? strategies.snowball.totalInterestPaid - strategies.avalanche.totalInterestPaid
      : 0;

  // Detect when all strategies produce the same payoff order (and thus identical results)
  const snowballOrder = [...accounts].sort((a, b) => a.balance - b.balance).map((a) => a.id);
  const avalancheOrder = [...accounts].sort((a, b) => b.rate - a.rate).map((a) => a.id);
  const snowballKey = snowballOrder.join(",");
  const avalancheKey = avalancheOrder.join(",");
  const customKey = customOrder.join(",");
  const strategiesIdentical =
    strategies.snowball &&
    strategies.avalanche &&
    Math.abs(strategies.snowball.totalInterestPaid - strategies.avalanche.totalInterestPaid) < 0.01 &&
    strategies.snowball.totalMonths === strategies.avalanche.totalMonths &&
    snowballKey === avalancheKey &&
    customKey === avalancheKey;

  const [activeTab, setActiveTab] = useState("planner"); // "planner" | "compare"
  const SCENARIO_COLORS = ["#4F7FFA", "#10B981", "#F97316"];
  const makeScenario = (label, extra, strat) => ({
    id: Math.random(), label, extraBudget: extra, strategy: strat,
    monthOverrides: {}, customOrder: accounts.map(a => a.id),
  });
  const [scenarios, setScenarios] = useState([makeScenario("Current Plan", extraBudget, activeStrategy)]);
  const updateScenario = (idx, changes) => setScenarios(prev => prev.map((s, i) => i === idx ? { ...s, ...changes } : s));

  const moveScenarioDebt = (scenarioIdx, debtIdx, dir) => {
    const order = [...(scenarios[scenarioIdx].customOrder || accounts.map(a => a.id))];
    const swapIdx = debtIdx + dir;
    if (swapIdx < 0 || swapIdx >= order.length) return;
    [order[debtIdx], order[swapIdx]] = [order[swapIdx], order[debtIdx]];
    updateScenario(scenarioIdx, { customOrder: order });
  };
  const runScenario = (scenario) => {
    let order;
    const safeCustomOrder = scenario.customOrder?.filter(id => accounts.find(a => a.id === id)) || accounts.map(a => a.id);
    if (scenario.strategy === "snowball") order = [...accounts].sort((a, b) => a.balance - b.balance).map(a => a.id);
    else if (scenario.strategy === "custom") order = safeCustomOrder;
    else order = [...accounts].sort((a, b) => b.rate - a.rate).map(a => a.id);
    const schedule = Object.entries(scenario.monthOverrides).map(([n, amt]) => ({
      id: `m-${n}`, fromMonth: parseInt(n), toMonth: parseInt(n), amount: amt,
    }));
    return runPayoffSimulation(accounts, scenario.extraBudget, order, schedule);
  };

  return (
    <div style={styles.page}>

      <main style={{ ...styles.main, ...(isMobile ? { padding: "16px 12px" } : {}) }}>
        <button style={styles.backBtn} onClick={onBack}>← Back to Dashboard</button>
        <div style={{ ...styles.pageTitle, ...(isMobile ? { fontSize: 20 } : {}) }}>Payoff Strategy Planner</div>
        <div style={styles.pageSubtitle}>
          Choose a strategy and set your monthly budget to see your personalized debt payoff plan.
        </div>

        {/* Tab switcher */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          {[{ id: "planner", label: "📊 Planner" }, { id: "compare", label: isPro ? "⚖️ Compare Scenarios" : "🔒 Compare Scenarios" }].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              background: activeTab === tab.id ? "#1a1a2e" : "#F3F4F6",
              color: activeTab === tab.id ? "#fff" : "#555",
              border: "none", borderRadius: 8, padding: "9px 18px",
              fontWeight: 600, fontSize: 14, cursor: "pointer",
            }}>{tab.label}</button>
          ))}
        </div>

        {activeTab === "compare" && !isPro && (
          <div style={{ padding: "20px 0" }}>
            <div style={{ background: "linear-gradient(135deg, #EEF2FF, #E0F2FE)", border: "1.5px solid #C7D2FE", borderRadius: 12, padding: "28px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>⚖️</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#1a1a2e", marginBottom: 6 }}>What-If Scenario Comparison</div>
              <div style={{ fontSize: 14, color: "#64748b", marginBottom: 20 }}>Compare up to 3 payoff strategies side by side — Pro feature</div>
              <button onClick={onUpgrade} style={{ background: "#4F7FFA", color: "#fff", border: "none", borderRadius: 10, padding: "11px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Upgrade to Pro · $2.99/mo</button>
            </div>
          </div>
        )}
        {activeTab === "compare" && isPro && (() => {
          const results = scenarios.map(runScenario);
          const baseline = results[0];
          const now = new Date();
          const months12 = Array.from({ length: 12 }, (_, i) => {
            const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
            return {
              key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`,
              label: d.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
              monthNum: i + 1,
            };
          });

          return (
            <div>
              {/* Scenario cards */}
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : `repeat(${scenarios.length}, 1fr)`, gap: 16, marginBottom: 24 }}>
                {scenarios.map((scenario, idx) => {
                  const result = results[idx];
                  const color = SCENARIO_COLORS[idx];
                  const monthsSaved = idx > 0 ? baseline.totalMonths - result.totalMonths : null;
                  const interestSaved = idx > 0 ? baseline.totalInterestPaid - result.totalInterestPaid : null;
                  return (
                    <div key={scenario.id} style={{ background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", overflow: "hidden", border: `2px solid ${color}22` }}>
                      <div style={{ background: color, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <input
                          value={scenario.label}
                          onChange={e => updateScenario(idx, { label: e.target.value })}
                          style={{ background: "transparent", border: "none", color: "#fff", fontSize: 15, fontWeight: 700, outline: "none", width: "100%" }}
                        />
                        {idx > 0 && (
                          <button onClick={() => setScenarios(prev => prev.filter((_, i) => i !== idx))}
                            style={{ background: "none", border: "none", color: "rgba(255,255,255,0.7)", fontSize: 18, cursor: "pointer", flexShrink: 0 }}>✕</button>
                        )}
                      </div>
                      <div style={{ padding: "16px" }}>
                        {/* Strategy */}
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", marginBottom: 6 }}>Strategy</div>
                          <div style={{ display: "flex", gap: 6 }}>
                            {[
                              { key: "avalanche", label: "🏔 Avalanche" },
                              { key: "snowball",  label: "⛄ Snowball"  },
                              { key: "custom",    label: "🎯 Custom"    },
                            ].map(s => (
                              <button key={s.key} onClick={() => updateScenario(idx, { strategy: s.key })} style={{
                                flex: 1, padding: "6px 0", borderRadius: 8, border: `1.5px solid ${scenario.strategy === s.key ? color : "#E5E7EB"}`,
                                background: scenario.strategy === s.key ? color + "15" : "#F8F9FC",
                                color: scenario.strategy === s.key ? color : "#888", fontSize: 11, fontWeight: 600, cursor: "pointer",
                              }}>{s.label}</button>
                            ))}
                          </div>

                          {/* Custom order list */}
                          {scenario.strategy === "custom" && (
                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                              {(scenario.customOrder || accounts.map(a => a.id)).map((id, debtIdx) => {
                                const acct = accounts.find(a => a.id === id);
                                const acctColor = COLORS[accounts.findIndex(a => a.id === id) % COLORS.length];
                                if (!acct) return null;
                                return (
                                  <div key={id} style={{ display: "flex", alignItems: "center", gap: 8, background: "#F8F9FC", borderRadius: 8, padding: "8px 10px" }}>
                                    <div style={{ width: 22, height: 22, borderRadius: "50%", background: acctColor, color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                      {debtIdx + 1}
                                    </div>
                                    <div style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>{acct.name}</div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                      <button onClick={() => moveScenarioDebt(idx, debtIdx, -1)} disabled={debtIdx === 0}
                                        style={{ background: "none", border: "1px solid #ddd", borderRadius: 4, padding: "1px 6px", cursor: "pointer", fontSize: 10, color: "#888", lineHeight: 1.4 }}>▲</button>
                                      <button onClick={() => moveScenarioDebt(idx, debtIdx, 1)} disabled={debtIdx === (scenario.customOrder || accounts.map(a => a.id)).length - 1}
                                        style={{ background: "none", border: "1px solid #ddd", borderRadius: 4, padding: "1px 6px", cursor: "pointer", fontSize: 10, color: "#888", lineHeight: 1.4 }}>▼</button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Extra budget */}
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", marginBottom: 6 }}>Extra Payment/mo</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontWeight: 700, color, fontSize: 16 }}>$</span>
                            <input type="number" min="0" value={scenario.extraBudget}
                              onChange={e => updateScenario(idx, { extraBudget: Math.max(0, parseFloat(e.target.value) || 0) })}
                              style={{ border: `1.5px solid ${color}55`, borderRadius: 8, padding: "7px 10px", fontSize: 15, fontWeight: 700, width: 100, color, outline: "none" }}
                            />
                            <span style={{ fontSize: 13, color: "#888" }}>/mo</span>
                          </div>
                        </div>

                        {/* Monthly overrides — compact */}
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", marginBottom: 6 }}>Monthly Overrides</div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 5 }}>
                            {months12.map(({ label, monthNum }) => {
                              const val = scenario.monthOverrides[monthNum] ?? "";
                              const hasCustom = val !== "";
                              return (
                                <div key={monthNum} style={{ background: hasCustom ? color + "15" : "#F8F9FC", border: `1px solid ${hasCustom ? color : "#E5E7EB"}`, borderRadius: 6, padding: "4px 5px", textAlign: "center" }}>
                                  <div style={{ fontSize: 9, fontWeight: 600, color: hasCustom ? color : "#aaa", marginBottom: 2 }}>{label}</div>
                                  <input
                                    key={`${monthNum}-${scenario.extraBudget}`}
                                    type="number" min="0" placeholder={String(scenario.extraBudget)}
                                    defaultValue={hasCustom ? val : ""}
                                    onBlur={e => {
                                      const parsed = parseFloat(e.target.value);
                                      const next = { ...scenario.monthOverrides };
                                      if (e.target.value === "" || isNaN(parsed) || parsed === scenario.extraBudget) delete next[monthNum];
                                      else next[monthNum] = parsed;
                                      updateScenario(idx, { monthOverrides: next });
                                    }}
                                    style={{ width: "100%", border: "none", background: "transparent", fontSize: 11, fontWeight: 700, textAlign: "center", color: hasCustom ? color : "#aaa", outline: "none" }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Results */}
                        <div style={{ background: "#F8F9FC", borderRadius: 10, padding: "12px 14px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                            <div>
                              <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>Payoff Date</div>
                              <div style={{ fontSize: 16, fontWeight: 800, color }}>{result.payoffDate}</div>
                              {monthsSaved !== null && monthsSaved !== 0 && (
                                <div style={{ fontSize: 11, color: monthsSaved > 0 ? "#10B981" : "#EF4444", fontWeight: 600 }}>
                                  {monthsSaved > 0 ? `▼ ${monthsSaved} mo sooner` : `▲ ${Math.abs(monthsSaved)} mo later`}
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>Total Interest</div>
                              <div style={{ fontSize: 16, fontWeight: 800, color: "#EF4444" }}>{formatCurrency(result.totalInterestPaid)}</div>
                              {interestSaved !== null && interestSaved !== 0 && (
                                <div style={{ fontSize: 11, color: interestSaved > 0 ? "#10B981" : "#EF4444", fontWeight: 600 }}>
                                  {interestSaved > 0 ? `▼ ${formatCurrency(interestSaved)} saved` : `▲ ${formatCurrency(Math.abs(interestSaved))} more`}
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Per-account payoff */}
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {accounts.map(acct => (
                              <div key={acct.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                                <span style={{ color: "#555" }}>{acct.name}</span>
                                <span style={{ fontWeight: 600, color }}>
                                  {result.payoffMonths[acct.id] ? `Month ${result.payoffMonths[acct.id]}` : "—"}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add scenario button */}
              {scenarios.length < 3 && (
                <button
                  onClick={() => setScenarios(prev => [...prev, makeScenario(`Scenario ${prev.length + 1}`, extraBudget, activeStrategy)])}
                  style={{ background: "#F8F9FC", border: "2px dashed #C7D2FE", borderRadius: 12, padding: "16px", width: "100%", fontSize: 14, fontWeight: 600, color: "#4F7FFA", cursor: "pointer", marginBottom: 24 }}
                >
                  + Add Scenario
                </button>
              )}

              {/* Comparison summary table */}
              {scenarios.length > 1 && (
                <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", overflow: "hidden", marginBottom: 24 }}>
                  <div style={{ padding: "16px 20px", borderBottom: "1px solid #F0F0F5", fontSize: 15, fontWeight: 700 }}>📋 Side-by-Side Summary</div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "#F8F9FC" }}>
                          <th style={{ padding: "10px 16px", textAlign: "left", color: "#888", fontWeight: 600 }}>Metric</th>
                          {scenarios.map((s, i) => (
                            <th key={s.id} style={{ padding: "10px 16px", textAlign: "right", color: SCENARIO_COLORS[i], fontWeight: 700 }}>{s.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { label: "Payoff Date", fn: (r) => r.payoffDate },
                          { label: "Total Months", fn: (r) => `${r.totalMonths} mo` },
                          { label: "Total Interest", fn: (r) => formatCurrency(r.totalInterestPaid) },
                          { label: "Total Paid", fn: (r) => formatCurrency(r.totalInterestPaid + accounts.reduce((s, a) => s + a.balance, 0)) },
                        ].map((row, i) => (
                          <tr key={i} style={{ borderTop: "1px solid #F0F0F5" }}>
                            <td style={{ padding: "10px 16px", color: "#555", fontWeight: 500 }}>{row.label}</td>
                            {results.map((r, j) => (
                              <td key={j} style={{ padding: "10px 16px", textAlign: "right", fontWeight: 600 }}>{row.fn(r)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === "planner" && (<div>

        {/* Budget Input */}
        <div style={{ ...styles.budgetCard, ...(isMobile ? { flexDirection: "column", gap: 12 } : {}) }}>
          <div style={styles.budgetLeft}>
            <div style={styles.budgetLabel}>Extra Payment Amount</div>
            <div style={styles.budgetDesc}>
              Added on top of all minimum payments ({formatCurrency(accounts.reduce((s, a) => s + a.minPayment, 0))}/mo)
            </div>
          </div>
          <div style={styles.budgetRight}>
            <span style={styles.budgetPrefix}>$</span>
            <input
              style={styles.budgetInput}
              type="number"
              min="0"
              value={extraBudget}
              onChange={(e) => updateExtraBudget(Math.max(0, Number(e.target.value)))}
            />
            <span style={styles.budgetSuffix}>/mo</span>
          </div>
        </div>

        {/* Variable Extra Payment Schedule */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showSchedule ? 20 : 0 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>📅 Variable Payment Schedule</div>
              <div style={{ fontSize: 13, color: "#888", marginTop: 3 }}>
                Set different extra amounts per month — e.g. a bonus, tax refund, or seasonal change
              </div>
            </div>
            <button
              onClick={() => isPro ? setShowSchedule(s => !s) : onUpgrade && onUpgrade()}
              style={{ background: "#EEF2FF", border: "1.5px solid #C7D2FE", borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600, color: "#4F7FFA", cursor: "pointer", whiteSpace: "nowrap", marginLeft: 16 }}
            >
              {showSchedule ? "▲ Hide" : (isPro ? "▼ Set Schedule" : "🔒 Pro Feature")}
            </button>
          </div>

          {showSchedule && (() => {
            // Build 12 months starting from current month
            const now = new Date();
            const months = Array.from({ length: 12 }, (_, i) => {
              const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
              const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
              const label = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
              const monthNum = i + 1; // month 1 = current month in simulation
              return { key, label, monthNum };
            });

            const getMonthAmount = (monthNum) => monthOverrides[monthNum] ?? "";
            const hasAnyCustom = Object.keys(monthOverrides).length > 0;

            return (
              <div>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>
                  Leave blank to use the default <strong>${extraBudget}/mo</strong> extra. Enter a custom amount to override that month.
                </div>

                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(6, 1fr)", gap: 10, marginBottom: 16 }}>
                  {months.map(({ key, label, monthNum }) => {
                    const val = getMonthAmount(monthNum);
                    const hasCustom = val !== "";
                    return (
                      <div key={key} style={{
                        background: hasCustom ? "#EEF2FF" : "#F8F9FC",
                        border: `1.5px solid ${hasCustom ? "#4F7FFA" : "#E5E7EB"}`,
                        borderRadius: 10, padding: "10px 8px", textAlign: "center",
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: hasCustom ? "#4F7FFA" : "#888", marginBottom: 6 }}>{label}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "center" }}>
                          <span style={{ fontSize: 12, color: "#aaa" }}>$</span>
                          <input
                            key={`${monthNum}-${extraBudget}`}
                            type="number" min="0" placeholder={String(extraBudget)}
                            style={{
                              width: "100%", border: "none", background: "transparent",
                              fontSize: 14, fontWeight: 700, textAlign: "center",
                              color: hasCustom ? "#4F7FFA" : "#aaa", outline: "none",
                            }}
                            defaultValue={hasCustom ? val : ""}
                            onBlur={(e) => setMonthAmount(monthNum, e.target.value, extraBudget)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {hasAnyCustom && (
                  <button
                    onClick={() => saveMonthOverrides({})}
                    style={{ background: "none", border: "none", color: "#bbb", fontSize: 12, cursor: "pointer", marginBottom: 12 }}
                  >
                    ✕ Clear all custom amounts
                  </button>
                )}

                {/* Impact preview */}
                {hasAnyCustom && baseline && current && (
                  <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "14px 16px", marginTop: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#166534", marginBottom: 10 }}>📊 Schedule Impact vs Flat ${extraBudget}/mo</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>Payoff Date</div>
                        <div style={{ fontSize: 15, fontWeight: 800 }}>{current.payoffDate}</div>
                        {current.totalMonths !== baseline[activeStrategy]?.totalMonths && (
                          <div style={{ fontSize: 12, color: current.totalMonths < baseline[activeStrategy]?.totalMonths ? "#10B981" : "#EF4444", marginTop: 2 }}>
                            {current.totalMonths < baseline[activeStrategy]?.totalMonths
                              ? `▼ ${baseline[activeStrategy].totalMonths - current.totalMonths} months sooner`
                              : `▲ ${current.totalMonths - baseline[activeStrategy].totalMonths} months later`}
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>Total Interest</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: "#EF4444" }}>{formatCurrency(current.totalInterestPaid)}</div>
                        {current.totalInterestPaid !== baseline[activeStrategy]?.totalInterestPaid && (
                          <div style={{ fontSize: 12, color: current.totalInterestPaid < baseline[activeStrategy]?.totalInterestPaid ? "#10B981" : "#EF4444", marginTop: 2 }}>
                            {current.totalInterestPaid < baseline[activeStrategy]?.totalInterestPaid
                              ? `▼ ${formatCurrency(baseline[activeStrategy].totalInterestPaid - current.totalInterestPaid)} saved`
                              : `▲ ${formatCurrency(current.totalInterestPaid - baseline[activeStrategy].totalInterestPaid)} more`}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Strategy Selector */}
        <div style={{ ...styles.strategyRow, ...(isMobile ? { gridTemplateColumns: "1fr" } : {}) }}>
          {strategyCards.map((s) => {
            const result = strategies[s.key];
            const isActive = activeStrategy === s.key;
            return (
              <div
                key={s.key}
                style={{
                  ...styles.strategyCard,
                  ...(isActive ? { borderColor: s.color, boxShadow: `0 0 0 3px ${s.color}22` } : { opacity: 0.85 }),
                }}
                onClick={() => updateStrategy(s.key)}
              >
                <div style={styles.strategyIcon}>{s.icon}</div>
                <div style={{ ...styles.strategyName, color: isActive ? s.color : "#1a1a2e" }}>
                  {s.label}
                </div>
                <div style={styles.strategyDesc}>{s.desc}</div>
                {result && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #F0F0F5", width: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
                      <span style={{ color: "#888" }}>Payoff date</span>
                      <span style={{ fontWeight: 700, color: s.color }}>{result.payoffDate}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
                      <span style={{ color: "#888" }}>Total interest</span>
                      <span style={{ fontWeight: 700, color: "#EF4444" }}>{formatCurrency(result.totalInterestPaid)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                      <span style={{ color: "#888" }}>Total months</span>
                      <span style={{ fontWeight: 700 }}>{result.totalMonths} mo</span>
                    </div>
                  </div>
                )}
                {isActive && (
                  <div style={{ ...styles.strategyBadge, background: s.color, marginTop: 12 }}>Selected</div>
                )}
              </div>
            );
          })}
        </div>

        {strategiesIdentical ? (
          <div style={{ background: "#F8F9FC", border: "1.5px solid #E5E7EB", borderRadius: 10, padding: "14px 18px", marginBottom: 24, fontSize: 13, color: "#555", lineHeight: 1.6 }}>
            💡 <strong>All three strategies show the same results</strong> because your debts happen to rank in the same order by both rate and balance — your highest-rate debt (<strong>{accounts.find(a => avalancheOrder[0] === a.id)?.name}</strong>) is also your smallest balance. Any strategy works equally well for your current debts.
          </div>
        ) : avalancheSavings > 0 ? (
          <div style={styles.savingsBanner}>
            💡 The <strong>Avalanche</strong> strategy saves you{" "}
            <strong style={{ color: "#10B981" }}>{formatCurrency(avalancheSavings)}</strong> in
            interest compared to Snowball — but Snowball may keep you more motivated with faster
            early wins!
          </div>
        ) : null}

        {/* Custom Order */}
        {/* Payoff Order */}
        {current && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>
              {strategyCards.find(s => s.key === activeStrategy)?.icon}{" "}
              {strategyCards.find(s => s.key === activeStrategy)?.label} — Payoff Order
            </div>
            <div style={styles.cardSubtitle}>
              {activeStrategy === "custom"
                ? "Use the arrows to set which debt you want to attack first."
                : strategyCards.find(s => s.key === activeStrategy)?.detail}
            </div>
            <div style={styles.payoffOrderList}>
              {activeStrategy === "custom"
                ? customOrder.map((id, idx) => {
                    const acct = accounts.find((a) => a.id === id);
                    const colorIdx = accounts.findIndex((a) => a.id === id);
                    const color = COLORS[colorIdx % COLORS.length];
                    const payoffMonth = current.payoffMonths[id];
                    return (
                      <div key={id} style={{ ...styles.payoffOrderRow, ...(isMobile ? { flexDirection: "column", alignItems: "flex-start", gap: 8 } : {}) }}>
                        <div style={styles.payoffOrderLeft}>
                          <div style={{ ...styles.customRank, background: color }}>{idx + 1}</div>
                          <div>
                            <div style={styles.payoffOrderName}>{acct?.name}</div>
                            <div style={styles.payoffOrderMeta}>{formatCurrency(acct?.balance)} · {acct?.rate}% APR</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          {payoffMonth && (
                            <div style={styles.payoffOrderRight}>
                              <div style={styles.payoffOrderMonth}>Month {payoffMonth}</div>
                              <div style={{ ...styles.payoffOrderDate, color }}>{getPayoffDate(payoffMonth)}</div>
                            </div>
                          )}
                          <div style={styles.customArrows}>
                            <button style={styles.arrowBtn} onClick={() => moveUp(idx)} disabled={idx === 0}>▲</button>
                            <button style={styles.arrowBtn} onClick={() => moveDown(idx)} disabled={idx === customOrder.length - 1}>▼</button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                : Object.entries(current.payoffMonths)
                    .sort((a, b) => a[1] - b[1])
                    .map(([id, month], idx) => {
                      const acct = accounts.find((a) => a.id === Number(id));
                      const colorIdx = accounts.findIndex((a) => a.id === Number(id));
                      const color = COLORS[colorIdx % COLORS.length];
                      return (
                        <div key={id} style={{ ...styles.payoffOrderRow, ...(isMobile ? { flexDirection: "column", alignItems: "flex-start", gap: 8 } : {}) }}>
                          <div style={styles.payoffOrderLeft}>
                            <div style={{ ...styles.payoffOrderNum, color, borderColor: color }}>{idx + 1}</div>
                            <div>
                              <div style={styles.payoffOrderName}>{acct?.name}</div>
                              <div style={styles.payoffOrderMeta}>{formatCurrency(acct?.balance)} · {acct?.rate}% APR</div>
                            </div>
                          </div>
                          <div style={styles.payoffOrderRight}>
                            <div style={styles.payoffOrderMonth}>Month {month}</div>
                            <div style={{ ...styles.payoffOrderDate, color }}>{getPayoffDate(month)}</div>
                          </div>
                        </div>
                      );
                    })
              }
            </div>
          </div>
        )}

        {/* Timeline */}
        {current && current.timeline.length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Balance Over Time</div>
            <div style={styles.cardSubtitle}>
              How each debt shrinks month by month (first 60 months shown)
            </div>
            <div style={styles.timelineWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Month</th>
                    {accounts.map((a, i) => (
                      <th key={a.id} style={{ ...styles.th, color: COLORS[i % COLORS.length] }}>
                        {a.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {current.timeline
                    .filter((_, i) => i % 3 === 0)
                    .map((row, i) => (
                      <tr key={i} style={i % 2 === 0 ? styles.trEven : {}}>
                        <td style={styles.td}>{row.month}</td>
                        {accounts.map((a) => {
                          const debt = row.debts.find((d) => d.id === a.id);
                          return (
                            <td key={a.id} style={styles.td}>
                              {debt?.remaining === 0 ? (
                                <span style={styles.paidBadge}>✓ Paid</span>
                              ) : (
                                formatCurrency(debt?.remaining || 0)
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </div>)}
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#F8F9FC", fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#1a1a2e" },
  header: { background: "#1a1a2e", padding: "0 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.15)" },
  headerInner: { maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", height: 64 },
  logo: { fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: 3 },
  logoSub: { fontSize: 11, color: "#8899BB", letterSpacing: 1, marginTop: 2, marginLeft: 10 },
  main: { maxWidth: 1100, margin: "0 auto", padding: "32px 24px" },
  scheduleInput: { border: "1.5px solid #E5E7EB", borderRadius: 8, padding: "7px 10px", fontSize: 14, outline: "none", boxSizing: "border-box" },
  backBtn: { background: "none", border: "1.5px solid #ddd", borderRadius: 8, padding: "8px 16px", fontSize: 14, cursor: "pointer", color: "#555", marginBottom: 24, fontWeight: 500 },
  pageTitle: { fontSize: 26, fontWeight: 800, marginBottom: 6 },
  pageSubtitle: { fontSize: 14, color: "#888", marginBottom: 28 },
  budgetCard: { background: "#fff", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 },
  budgetLeft: { flex: 1 },
  budgetLabel: { fontSize: 15, fontWeight: 700, marginBottom: 4 },
  budgetDesc: { fontSize: 13, color: "#888" },
  budgetRight: { display: "flex", alignItems: "center", gap: 6 },
  budgetPrefix: { fontSize: 20, fontWeight: 700, color: "#4F7FFA" },
  budgetInput: { border: "2px solid #4F7FFA", borderRadius: 8, padding: "8px 12px", fontSize: 20, fontWeight: 700, width: 110, color: "#1a1a2e", outline: "none", textAlign: "center" },
  budgetSuffix: { fontSize: 14, color: "#888" },
  strategyRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 },
  strategyCard: { background: "#fff", borderRadius: 14, padding: "20px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", border: "2px solid transparent", cursor: "pointer", textAlign: "center", position: "relative", transition: "all 0.2s" },
  strategyIcon: { fontSize: 28, marginBottom: 8 },
  strategyName: { fontSize: 16, fontWeight: 700, marginBottom: 4 },
  strategyDesc: { fontSize: 13, color: "#888" },
  strategyBadge: { display: "inline-block", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, marginTop: 10 },
  comparisonGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 },
  compCard: { background: "#fff", borderRadius: 12, padding: "16px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", cursor: "pointer", borderTop: "3px solid #eee", transition: "all 0.2s" },
  compLabel: { fontSize: 14, fontWeight: 700, marginBottom: 12 },
  compStat: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: "1px solid #F5F5F5" },
  compStatLabel: { fontSize: 12, color: "#999" },
  compStatValue: { fontSize: 13, fontWeight: 700 },
  savingsBanner: { background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "14px 18px", fontSize: 13, color: "#166534", marginBottom: 24, lineHeight: 1.6 },
  card: { background: "#fff", borderRadius: 14, padding: "24px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", marginBottom: 24 },
  cardTitle: { fontSize: 16, fontWeight: 700, marginBottom: 4 },
  cardSubtitle: { fontSize: 13, color: "#888", marginBottom: 20 },
  customRow: { display: "flex", alignItems: "center", gap: 14, padding: "12px 0", borderBottom: "1px solid #F5F5F5" },
  customRank: { width: 28, height: 28, borderRadius: "50%", color: "#fff", fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  customInfo: { flex: 1 },
  customName: { fontSize: 14, fontWeight: 600 },
  customMeta: { fontSize: 12, color: "#999" },
  customArrows: { display: "flex", flexDirection: "column", gap: 2 },
  arrowBtn: { background: "#F3F4F6", border: "none", borderRadius: 4, padding: "2px 8px", cursor: "pointer", fontSize: 11, color: "#555" },
  payoffOrderList: { display: "flex", flexDirection: "column", gap: 12 },
  payoffOrderRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#F8F9FC", borderRadius: 10 },
  payoffOrderLeft: { display: "flex", alignItems: "center", gap: 14 },
  payoffOrderNum: { width: 30, height: 30, borderRadius: "50%", border: "2px solid", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 },
  payoffOrderName: { fontSize: 14, fontWeight: 600 },
  payoffOrderMeta: { fontSize: 12, color: "#999" },
  payoffOrderRight: { textAlign: "right" },
  payoffOrderMonth: { fontSize: 12, color: "#999" },
  payoffOrderDate: { fontSize: 14, fontWeight: 700 },
  timelineWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", borderBottom: "2px solid #F0F0F5", color: "#888" },
  td: { padding: "9px 12px", borderBottom: "1px solid #F8F9FC" },
  trEven: { background: "#FAFAFA" },
  paidBadge: { background: "#D1FAE5", color: "#065F46", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20 },
};
